// The one fetch wrapper (Build Book §11a).
//
// A static contract check bans bare `fetch(` anywhere outside this file,
// because a stray fetch misses the refresh mutex — and the refresh mutex is
// the difference between a working session and a cashier thrown out mid-bill.
//
// Trap · the logout nobody can reproduce
// Without the mutex, three parallel 401s fire three refreshes. Two of them
// replay a token the first already rotated, the server's reuse detection
// correctly concludes theft, revokes the family — and the cashier is signed
// out in the middle of a sale. One refresh in flight per tab, ever.

/** The §07 error envelope, as a throwable. */
export class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || 'Something went wrong.')
    this.name = 'ApiError'
    this.status = status
    // The ONLY thing a client may branch on. Never pattern-match on prose.
    this.code = body?.code || 'UNKNOWN'
    this.fields = body?.fields || {}
    this.existing = body?.existing || null
    this.requestId = body?.request_id || null
  }
}

/** Thrown when the request never reached the server, or the answer was lost. */
export class NetworkError extends Error {
  constructor() {
    super("We didn't hear back from the server.")
    this.name = 'NetworkError'
    this.code = 'NETWORK'
  }
}

let refreshInFlight = null

function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Cleared only after the promise settles, so every caller that arrived
        // during the flight awaits this same one.
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

async function parse(response) {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    // A non-JSON body from an /api path means something upstream answered
    // instead of the app — a proxy error page, usually. Say so rather than
    // reporting a JSON parse failure.
    return { message: 'The server sent an unexpected response.', code: 'BAD_RESPONSE' }
  }
}

/**
 * @param {string} path        e.g. '/api/products'
 * @param {object} options
 * @param {string} options.method
 * @param {object} options.body            serialised as JSON
 * @param {string} options.idempotencyKey  required by every posting route
 * @param {boolean} options._retried       internal
 */
export async function api(path, options = {}) {
  const { method = 'GET', body, idempotencyKey, signal, _retried = false } = options

  // A file upload is multipart, and multipart carries a boundary the browser
  // has to generate — so FormData passes through untouched and its
  // Content-Type is left unset on purpose. Setting it by hand produces a
  // boundary-less header and the server parses zero fields, which presents as
  // "the file was empty".
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData

  const headers = { Accept: 'application/json' }
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey

  let response
  try {
    response = await fetch(path, {
      method,
      headers,
      signal,
      // Same origin in both dev and production — the API serves this build.
      credentials: 'same-origin',
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    })
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    throw new NetworkError()
  }

  if (response.status === 401 && !_retried && !path.startsWith('/api/auth/')) {
    const refreshed = await refreshOnce()
    if (refreshed) {
      return api(path, { ...options, _retried: true })
    }
  }

  const payload = await parse(response)

  if (!response.ok) {
    throw new ApiError(response.status, payload)
  }
  return payload
}

/**
 * A rendered document rather than JSON: the tax invoice off
 * `GET /api/sales/{id}/pdf`.
 *
 * It is here and not in a feature because of the mutex above. The invoice is
 * the one thing a cashier fetches after standing at the counter for a while,
 * which makes it the request most likely to arrive on an expired access token —
 * a bare `fetch` for it would miss the single-flight refresh and be exactly the
 * "logout nobody can reproduce" this file exists to prevent.
 *
 * The server picks the media type: a PDF where the deployment has a renderer,
 * the print-ready page where it has none, and `X-Document-Renderer` says which
 * so the caller can print the second one itself rather than handing a browser
 * an HTML file called invoice.pdf.
 *
 * @returns {Promise<{blob: Blob, isPdf: boolean, renderer: string}>}
 */
export async function getDocument(path, options = {}) {
  const { signal, _retried = false } = options

  let response
  try {
    response = await fetch(path, {
      headers: { Accept: 'application/pdf, text/html' },
      signal,
      credentials: 'same-origin',
    })
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    throw new NetworkError()
  }

  if (response.status === 401 && !_retried) {
    const refreshed = await refreshOnce()
    if (refreshed) return getDocument(path, { ...options, _retried: true })
  }

  // A refusal is JSON in the one envelope even on this route, so it is read the
  // same way every other error is.
  if (!response.ok) throw new ApiError(response.status, await parse(response))

  const type = response.headers.get('Content-Type') || ''
  return {
    blob: await response.blob(),
    isPdf: type.startsWith('application/pdf'),
    renderer: response.headers.get('X-Document-Renderer') || 'unknown',
  }
}

/**
 * A file the server renders for saving, such as a CSV template, as a Blob.
 *
 * Through the same single-flight refresh as everything else, for the reason
 * getDocument gives. A plain `<a href download>` cannot renew a session: after
 * fifteen idle minutes its request carries an expired access cookie, and the
 * browser saves the refusal under the template's name.
 *
 * @returns {Promise<{blob: Blob, filename: string|null}>}
 */
export async function getFile(path, options = {}) {
  const { signal, _retried = false } = options

  let response
  try {
    response = await fetch(path, { signal, credentials: 'same-origin' })
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    throw new NetworkError()
  }

  if (response.status === 401 && !_retried) {
    const refreshed = await refreshOnce()
    if (refreshed) return getFile(path, { ...options, _retried: true })
  }

  if (!response.ok) throw new ApiError(response.status, await parse(response))

  const disposition = response.headers.get('Content-Disposition') || ''
  const named = /filename="?([^";]+)"?/i.exec(disposition)
  return { blob: await response.blob(), filename: named ? named[1] : null }
}

export const get = (path, options) => api(path, { ...options, method: 'GET' })
export const post = (path, body, options) => api(path, { ...options, method: 'POST', body })
export const patch = (path, body, options) => api(path, { ...options, method: 'PATCH', body })
